import { nowUTC } from "@homebound/activesupport";
import { ContentHeader, HeaderAction } from "@homebound/beam";
import { useCallback } from "react";
import { cycleTrackingAction } from "src/components/CycleTrackingAction";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, PlanPackageIncomingSlotStatus, SavePlanPackageInput } from "src/generated/graphqlTypes";
import { useSavePlanPackageTakeoffsCycleMutation } from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import { useTakeoffsStore } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { createPlanPackageIncomingSlotReviewUrl } from "src/RouteUrls";
import { pluralize } from "src/utils";

export function TakeoffsTabHeader() {
  const planPackage = useTakeoffsStore((state) => state.planPackage);
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);
  const { id, version, incomingSlots, takeoffsStartedAt, takeoffsCompletedAt } = planPackage;

  const [saveTakeoffsCycle] = useSavePlanPackageTakeoffsCycleMutation();
  const saveCycle = useCallback(
    (input: Pick<SavePlanPackageInput, "takeoffsStartedAt" | "takeoffsCompletedAt">) =>
      saveTakeoffsCycle({ variables: { input: { id, ...input } } }),
    [saveTakeoffsCycle, id],
  );

  const pendingSlotCount = incomingSlots.count((s) => s.status.code === PlanPackageIncomingSlotStatus.Pending);
  const reviewSlotsAction: HeaderAction = {
    label: `${pendingSlotCount} ${pluralize(pendingSlotCount, "slot")} to review`,
    variant: "caution",
    icon: "todo",
    onClick: createPlanPackageIncomingSlotReviewUrl(id, version.id),
  };
  const actions: HeaderAction[] = [
    ...(designerModifiedSlotsEnabled && pendingSlotCount > 0 ? [reviewSlotsAction] : []),
    cycleTrackingAction({
      title: "Plan Cycle",
      startedAt: takeoffsStartedAt?.toPlainDate(),
      completedAt: takeoffsCompletedAt?.toPlainDate(),
      onStart: () => saveCycle({ takeoffsStartedAt: nowUTC() }),
      onMarkComplete: () => saveCycle({ takeoffsCompletedAt: nowUTC() }),
      onReopen: () => saveCycle({ takeoffsCompletedAt: null }),
      onReset: () => saveCycle({ takeoffsStartedAt: null, takeoffsCompletedAt: null }),
    }),
  ];

  return <ContentHeader title="Takeoffs" actions={actions} />;
}
